import { useState } from 'react';
import { api, ApiError } from '../lib/api.js';
import { useAi } from '../lib/ai.js';
import { VoiceRecorder, type Recording } from './VoiceRecorder.js';

/**
 * Write about a day after the fact (docs/today-rework-2026-10-14 §2 E; built
 * 14 Oct 2026 on Nick's "let's try it", to be revisited with him).
 *
 * Lives on a finished day's panel in Days and nowhere else. The entry is
 * filed under that day — the only thing that may be filed late; a check-in
 * never is — and the list shows it with when it was written. The server
 * stamps the day's line for one more read and lets the week's reading go
 * (services/late-entry.ts). No sorter here: the door is for today.
 */
export function PastDayEntry({ day, onSaved }: { day: string; onSaved: () => void }): JSX.Element {
  const { canTranscribe } = useAi();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'speak' | 'write'>(canTranscribe ? 'speak' : 'write');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dayName = new Date(`${day}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });

  async function saveText(): Promise<void> {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    setError(null);
    try {
      await api.createTextEntry({ text: t, day });
      setText('');
      setSaved(true);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save that.");
    } finally {
      setBusy(false);
    }
  }

  async function saveRecording(rec: Recording): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.uploadRecording(rec.blob, { kind: 'daily_journal', durationSeconds: rec.durationSeconds, day });
      setSaved(true);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? `${err.message} Your recording is still here.` : "Couldn't save that recording.");
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className="linkish" onClick={() => setOpen(true)}>
        Write about this day ›
      </button>
    );
  }
  return (
    <section className="card stack-sm stack" aria-label={`Write about ${dayName}`}>
      <div className="row-between">
        <strong>Write about {dayName}</strong>
        {canTranscribe && (
          <div className="row" style={{ gap: 'var(--space-2)' }}>
            <button type="button" className="chip" aria-pressed={mode === 'speak'} onClick={() => setMode('speak')}>Speak</button>
            <button type="button" className="chip" aria-pressed={mode === 'write'} onClick={() => setMode('write')}>Write</button>
          </div>
        )}
      </div>
      <p className="faint" style={{ margin: 0 }}>
        Filed under this day, and shown with when it was written. If the day has a line, it is read again the next time you open it.
      </p>
      {saved ? (
        <p className="muted" style={{ margin: 0 }}>
          Saved.{' '}
          <button type="button" className="linkish linkish-quiet" onClick={() => setSaved(false)}>Write more</button>
        </p>
      ) : mode === 'speak' && canTranscribe ? (
        <VoiceRecorder onComplete={(rec) => void saveRecording(rec)} disabled={busy} />
      ) : (
        <>
          <label htmlFor={`past-${day}`} className="sr-only">Write about {dayName}</label>
          <textarea id={`past-${day}`} className="textarea" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="What that day was, in any order." />
          <div className="row" style={{ gap: 'var(--space-2)' }}>
            <button type="button" className="btn btn-primary grow" disabled={!text.trim() || busy} onClick={() => void saveText()}>Save</button>
            <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)}>Not now</button>
          </div>
        </>
      )}
      {error && <p className="faint" role="alert">{error}</p>}
    </section>
  );
}
